import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Percent, DollarSign, Store, Package, FolderTree, X } from 'lucide-react';
import { MyContext } from '../../../context';
import ModeCard from '../../../components/ModeCard';
import SimpleDropdown from '../../../components/SimpleDropdown';
import ProductSearchInput from '../../../components/ProductSearchInput';
import { getCoupon, createCoupon, updateCoupon, listCategories, listProducts } from '../../../utils/queries';

const emptyForm = {
	code: '',
	discountType: 'percentage',
	discountValue: '',
	discountUnit: 'currency',
	scopeType: 'store',
	startsAt: '',
	endsAt: '',
	usageLimit: '',
	minCartItems: '',
	maxCartItems: '',
};

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" with no timezone
// suffix; the API returns/accepts full ISO strings.
const toDatetimeLocal = (iso) => (iso ? iso.slice(0, 16) : '');
const toIso = (local) => (local ? new Date(local).toISOString() : null);
const toIntOrNull = (value) => (value === '' || value == null ? null : Number(value));

export default function CouponEditor() {
	const { id } = useParams();
	const navigate = useNavigate();
	const isNew = !id;
	const { activeStoreId } = useContext(MyContext);

	const [form, setForm] = useState(emptyForm);
	const [scopeProducts, setScopeProducts] = useState([]); // [{productId, name, productNumber}]
	const [scopeCategoryIds, setScopeCategoryIds] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(!isNew);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!activeStoreId) return;
		listCategories({ storeId: activeStoreId, limit: 200 }).then((result) => {
			if (result?.success) setCategories(result.data.categories);
		});
	}, [activeStoreId]);

	useEffect(() => {
		if (isNew) return;
		setLoading(true);
		getCoupon(id).then(async (result) => {
			if (!result?.success) {
				setLoading(false);
				return;
			}
			const coupon = result.data;
			setForm({
				code: coupon.code,
				discountType: coupon.discountType,
				discountValue: coupon.discountValue,
				discountUnit: coupon.discountUnit,
				scopeType: coupon.scopeType,
				startsAt: toDatetimeLocal(coupon.startsAt),
				endsAt: toDatetimeLocal(coupon.endsAt),
				usageLimit: coupon.usageLimit ?? '',
				minCartItems: coupon.minCartItems ?? '',
				maxCartItems: coupon.maxCartItems ?? '',
			});
			setScopeCategoryIds(
				(coupon.scopeItems || []).map((item) => item.categoryId).filter((v) => v != null),
			);

			const productIds = (coupon.scopeItems || []).map((item) => item.productId).filter((v) => v != null);
			if (productIds.length) {
				const productsResult = await listProducts({ storeId: coupon.storeId, limit: 200 });
				const byId = new Map((productsResult?.data?.products || []).map((p) => [p.productId, p]));
				setScopeProducts(
					productIds.map((productId) => {
						const product = byId.get(productId);
						return { productId, name: product?.name || `Product #${productId}`, productNumber: product?.productNumber };
					}),
				);
			}
			setLoading(false);
		});
	}, [id, isNew]);

	const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

	const addScopeProduct = (product) => {
		setScopeProducts((prev) => (prev.some((p) => p.productId === product.productId) ? prev : [...prev, product]));
	};
	const removeScopeProduct = (productId) => setScopeProducts((prev) => prev.filter((p) => p.productId !== productId));

	const toggleScopeCategory = (categoryId) => {
		setScopeCategoryIds((prev) =>
			prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId],
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const scopeItems =
			form.scopeType === 'products'
				? scopeProducts.map((p) => ({ productId: p.productId }))
				: form.scopeType === 'categories'
					? scopeCategoryIds.map((categoryId) => ({ categoryId }))
					: undefined;

		const payload = {
			code: form.code.trim(),
			discountType: form.discountType,
			discountValue: Number(form.discountValue),
			discountUnit: form.discountType === 'percentage' ? 'currency' : form.discountUnit,
			scopeType: form.scopeType,
			startsAt: toIso(form.startsAt),
			endsAt: toIso(form.endsAt),
			usageLimit: toIntOrNull(form.usageLimit),
			minCartItems: toIntOrNull(form.minCartItems),
			maxCartItems: toIntOrNull(form.maxCartItems),
			scopeItems,
		};

		setSaving(true);
		const result = isNew
			? await createCoupon({ ...payload, storeId: activeStoreId })
			: await updateCoupon(id, payload);
		setSaving(false);

		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to save.');
			return;
		}
		toast.success('Saved.');
		if (isNew) {
			navigate(`/coupons/${result.data.couponId}/edit`, { replace: true });
		}
	};

	if (loading) return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;

	return (
		<div className='py-8 pr-8 max-w-2xl'>
			<div className='mb-6'>
				<Link to='/coupons' className='text-sm text-gray-500 hover:underline'>
					&larr; All Coupon Codes
				</Link>
				<h1 className='text-2xl font-light tracking-tight mt-1'>{isNew ? 'New coupon' : 'Edit coupon'}</h1>
				<p className='text-sm text-gray-500'>Create a discount code for your store</p>
			</div>

			<form onSubmit={handleSubmit} className='space-y-8'>
				<div>
					<label>Enter coupon code</label>
					<input
						className='form-input'
						required
						value={form.code}
						onChange={(e) => setField('code', e.target.value.toUpperCase())}
						placeholder='SUMMER10'
					/>
					<p className='text-xs text-gray-400 mt-1'>Customers enter this at checkout — e.g. SUMMER10</p>
				</div>

				<div>
					<h2 className='font-medium mb-3'>Discount</h2>
					<label>Type</label>
					<div className='grid grid-cols-2 gap-4 mb-4'>
						<ModeCard
							icon={<Percent className='size-4' />}
							label='Percentage'
							description='Take a percentage off qualifying items'
							selected={form.discountType === 'percentage'}
							onClick={() => setField('discountType', 'percentage')}
						/>
						<ModeCard
							icon={<DollarSign className='size-4' />}
							label='Fixed amount'
							description='Take a set amount off the order'
							selected={form.discountType === 'fixed_amount'}
							onClick={() => setField('discountType', 'fixed_amount')}
						/>
					</div>

					<label>Amount</label>
					<div className='flex gap-2 max-w-xs'>
						<input
							type='number'
							min='0'
							step='0.01'
							required
							className='form-input'
							value={form.discountValue}
							onChange={(e) => setField('discountValue', e.target.value)}
						/>
						{form.discountType === 'fixed_amount' ? (
							<SimpleDropdown
								className='w-32 shrink-0'
								value={form.discountUnit}
								onChange={(value) => setField('discountUnit', value)}
								options={[
									{ value: 'currency', label: 'USD' },
									{ value: 'points', label: 'Points' },
								]}
							/>
						) : (
							<span className='form-input w-16 shrink-0 flex items-center justify-center text-gray-400'>%</span>
						)}
					</div>
				</div>

				<div>
					<h2 className='font-medium mb-3'>Conditions</h2>

					<label>Active period</label>
					<div className='flex items-center gap-2 mb-4'>
						<input
							type='datetime-local'
							className='form-input'
							value={form.startsAt}
							onChange={(e) => setField('startsAt', e.target.value)}
						/>
						<span className='text-gray-400'>&rarr;</span>
						<input
							type='datetime-local'
							className='form-input'
							value={form.endsAt}
							onChange={(e) => setField('endsAt', e.target.value)}
						/>
					</div>
					<p className='text-xs text-gray-400 -mt-3 mb-4'>Leave empty to start immediately and never expire</p>

					<div className='grid grid-cols-2 gap-4'>
						<div>
							<label>Usage limit</label>
							<input
								type='number'
								min='1'
								className='form-input'
								placeholder='Unlimited'
								value={form.usageLimit}
								onChange={(e) => setField('usageLimit', e.target.value)}
							/>
							<p className='text-xs text-gray-400 mt-1'>Total number of times this code can be used</p>
						</div>
						<div>
							<label>Cart item count</label>
							<div className='flex items-center gap-2'>
								<input
									type='number'
									min='0'
									className='form-input'
									placeholder='Min'
									value={form.minCartItems}
									onChange={(e) => setField('minCartItems', e.target.value)}
								/>
								<span className='text-gray-400'>&ndash;</span>
								<input
									type='number'
									min='0'
									className='form-input'
									placeholder='Max'
									value={form.maxCartItems}
									onChange={(e) => setField('maxCartItems', e.target.value)}
								/>
							</div>
							<p className='text-xs text-gray-400 mt-1'>Items required in the cart; leave empty for no limit</p>
						</div>
					</div>
				</div>

				<div>
					<h2 className='font-medium mb-1'>Scope</h2>
					<p className='text-xs text-gray-400 mb-3'>
						Choose whether this coupon applies to your whole store or only to selected products or categories. A
						cart item qualifies if it matches the selected scope.
					</p>
					<div className='grid grid-cols-3 gap-4 mb-4'>
						<ModeCard
							icon={<Store className='size-4' />}
							label='Entire store'
							description='Applies to every product — no restrictions'
							selected={form.scopeType === 'store'}
							onClick={() => setField('scopeType', 'store')}
						/>
						<ModeCard
							icon={<Package className='size-4' />}
							label='Products'
							description='Only specific products'
							selected={form.scopeType === 'products'}
							onClick={() => setField('scopeType', 'products')}
						/>
						<ModeCard
							icon={<FolderTree className='size-4' />}
							label='Categories'
							description='Products in chosen categories'
							selected={form.scopeType === 'categories'}
							onClick={() => setField('scopeType', 'categories')}
						/>
					</div>

					{form.scopeType === 'products' && (
						<div className='space-y-2'>
							<ProductSearchInput
								storeId={activeStoreId}
								excludeProductIds={scopeProducts.map((p) => p.productId)}
								onSelect={addScopeProduct}
								placeholder='Select products...'
							/>
							{scopeProducts.length === 0 && (
								<p className='text-xs text-gray-400'>Nothing selected yet — pick at least one.</p>
							)}
							{scopeProducts.map((product) => (
								<div
									key={product.productId}
									className='flex items-center justify-between border border-gray-200 rounded-md px-3 py-2'
								>
									<div>
										<p className='text-sm text-black'>{product.name}</p>
										{product.productNumber && <p className='text-xs text-gray-400'>{product.productNumber}</p>}
									</div>
									<button type='button' className='text-gray-400 hover:text-gray-600' onClick={() => removeScopeProduct(product.productId)}>
										<X className='size-4' />
									</button>
								</div>
							))}
						</div>
					)}

					{form.scopeType === 'categories' && (
						<div className='space-y-1 border border-gray-200 rounded-md p-3 max-h-64 overflow-y-auto'>
							{categories.length === 0 && <p className='text-xs text-gray-400'>No categories yet.</p>}
							{categories.map((category) => (
								<label key={category.categoryId} className='flex items-center gap-2 text-sm py-1 cursor-pointer'>
									<input
										type='checkbox'
										className='size-4 rounded border-gray-300 accent-gray-400'
										checked={scopeCategoryIds.includes(category.categoryId)}
										onChange={() => toggleScopeCategory(category.categoryId)}
									/>
									{category.name}
								</label>
							))}
						</div>
					)}
				</div>

				<div className='flex justify-end gap-3 pt-2'>
					<Link to='/coupons' className='btn-secondary'>
						Cancel
					</Link>
					<button type='submit' disabled={saving} className='btn-primary'>
						{saving ? 'Saving...' : 'Save'}
					</button>
				</div>
			</form>
		</div>
	);
}
